<script setup>
import { computed, nextTick, ref } from 'vue';
import XlButton from '../xl/XlButton.vue';
import IconDownload from '../icons/IconDownload.vue';
import IconFile from '../icons/IconFile.vue';
import useResourceDownload from '../../composables/useResourceDownload';
import { typeSize } from '../../composables/usePortalLibrary';
import { downloadErrorMessage, downloadFailureIsStale } from '../../helpers/helper';

/**
 * A session's files (XN-4745): the count in the heading, the first five, and
 * the rest one click away, so a long list never pushes the actions out of
 * sight. Each downloads through the per-file gate. A file that has gone, or
 * is no longer theirs, leaves the list, as on the Library item, and the
 * caller hears of it (`removed`). No files, no block: no heading, no empty
 * state. The last one going takes the block, and the keyboard's place, with
 * it, so the caller is asked to take focus (`emptied`).
 */
const SHOWN = 5;
const props = defineProps({
  files: { type: Array, default: () => [] },
  level: { type: Number, default: 3 },
});
const emit = defineEmits(['removed', 'emptied']);

const gone = ref(new Set());
const all = ref(false);
const busy = ref(new Set());
const root = ref(null);
const listed = computed(() => props.files.filter((file) => !gone.value.has(file.id)));
const shown = computed(() => (all.value ? listed.value : listed.value.slice(0, SHOWN)));
const { download } = useResourceDownload();

// A button that leaves the page takes the keyboard's place with it; hand it on.
const focusDownload = async (index) => {
  await nextTick();
  const buttons = root.value?.querySelectorAll('[data-testid="file-download"]') ?? [];
  buttons[Math.min(index, buttons.length - 1)]?.focus();
};
const showAll = () => { all.value = true; focusDownload(SHOWN); };

const setBusy = (id, on) => {
  const next = new Set(busy.value);
  next[on ? 'add' : 'delete'](id);
  busy.value = next;
};

async function save(file) {
  if (busy.value.has(file.id)) return;
  setBusy(file.id, true);
  try {
    await download(file);
  } catch (error) {
    window.loadNotification?.({ message: downloadErrorMessage(error), error: true });
    if (downloadFailureIsStale(error)) {
      const at = shown.value.findIndex((row) => row.id === file.id);
      gone.value = new Set([...gone.value, file.id]);
      emit('removed', file.id);
      // Only if focus is still here (or lost with the disabled button), not if the user moved on.
      const active = document.activeElement;
      if (active === document.body || root.value?.contains(active)) {
        if (listed.value.length) focusDownload(at); else emit('emptied');
      }
    }
  } finally {
    setBusy(file.id, false);
  }
}
</script>

<template>
  <section v-if="listed.length" ref="root" class="files" data-testid="session-files">
    <component :is="`h${level}`" class="files__h">Files <span class="files__count">· {{ listed.length }}</span></component>
    <div v-for="file in shown" :key="file.id" class="files__row" data-testid="session-file">
      <span class="files__tile" aria-hidden="true"><IconFile color="currentColor" size="17" /></span>
      <span class="files__text">
        <b class="files__name" :title="file.name">{{ file.name }}</b>
        <span class="files__meta">{{ typeSize(file) }}</span>
      </span>
      <XlButton variant="ghost" size="sm" :loading="busy.has(file.id)" data-testid="file-download" @click="save(file)">
        <template #icon><IconDownload color="currentColor" size="13" /></template>
        Download<span class="files__sr">: {{ file.name }}</span>
      </XlButton>
    </div>
    <XlButton
      v-if="!all && listed.length > SHOWN"
      variant="ghost"
      size="sm"
      class="files__more"
      data-testid="files-show-all"
      @click="showAll"
    >Show all {{ listed.length }} files</XlButton>
  </section>
</template>

<style scoped>
/* Prototype cpDetails (build 15 Oct): "Files · N", rows on the panel's own
   background, no card and no rules; rows as PortalSessionSpeakers has them. */
.files__h { margin: 0 0 9px; font-size: 13px; font-weight: 700; color: var(--xl-color-on-surface); }
/* The prototype's --mut is 3.8:1 on paper; small text needs 4.5:1 (AA). */
.files__count { font-weight: 500; color: var(--xl-color-secondary-text); }
.files__row { display: flex; align-items: flex-start; gap: 10px; min-height: 52px; padding: 6px 0; box-sizing: border-box; }
.files__row > :deep(.xl-btn) { align-self: center; }
/* The Library row's purple file tile (prototype `ico b`). */
.files__tile {
  width: 32px; height: 32px; flex: 0 0 32px; border-radius: 10px; display: grid; place-items: center;
  background: var(--xl-color-primary-container); color: var(--xl-color-primary);
}
.files__text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.files__name { font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.files__meta { font-size: 12.5px; color: var(--xl-color-secondary-text); }
.files__more { margin-top: 8px; }
.files__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
</style>
